import aiProviderConfig from './config/aiProviders.json';

export type ID = string;

export const uid = (): ID =>
  (crypto as any).randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// 'posts' is intentionally left out of the selectable set for now (Posts tab
// is hidden - see ChatScreen) but stays a valid stored value so existing
// data/routes for it aren't broken, just unreachable from the tab UI.
export type ChannelViewType = 'messages' | 'charts' | 'reports' | 'posts';
export const CHANNEL_VIEW_TYPES: ChannelViewType[] = ['messages', 'charts', 'reports'];

export type ChartVariant = 'number' | 'bar' | 'line' | 'circle';
export const CHART_VARIANTS: ChartVariant[] = ['number', 'bar', 'line', 'circle'];

// Owner-selectable look for the public /shared/{nickname}/{token} view
// (PublicChannelViewScreen.tsx). "classic" is the original plain card
// list; "report" is a hero + stat-tiles + chart layout, all computed from
// the channel's real signals (see PublicReportDesign.tsx) - no hand-
// written narrative copy, unlike the separate landing/ marketing site's
// static per-channel pages, which this is not related to.
export type ChannelViewDesign = 'classic' | 'report';
export const CHANNEL_VIEW_DESIGNS: ChannelViewDesign[] = ['classic', 'report'];

export type ThemeMode = 'system' | 'light' | 'dark';
export const THEME_MODES: ThemeMode[] = ['system', 'light', 'dark'];

export type AppLanguage = 'english' | 'ukrainian';
export const APP_LANGUAGES: AppLanguage[] = ['english', 'ukrainian'];

// Message bunching (per-channel - Channel.bunchingInterval, set from a
// channel's edit form): consecutive messages from the same sender arriving
// within this many seconds of each other collapse into one stack in that
// channel's chat. 0 means off. Default is 60 (1 min).
export const BUNCHING_INTERVALS = [0, 10, 15, 20, 25, 30, 45, 60, 120, 180, 300, 600, 900] as const;
export type BunchingInterval = (typeof BUNCHING_INTERVALS)[number];
export const DEFAULT_BUNCHING_INTERVAL: BunchingInterval = 60;
export const BUNCHING_INTERVAL_LABEL: Record<BunchingInterval, string> = {
  0: 'Off',
  10: '10 seconds',
  15: '15 seconds',
  20: '20 seconds',
  25: '25 seconds',
  30: '30 seconds',
  45: '45 seconds',
  60: '1 minute',
  120: '2 minutes',
  180: '3 minutes',
  300: '5 minutes',
  600: '10 minutes',
  900: '15 minutes',
};

export const label = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

// AI reports (backend modules/report/* + POST /channels/{nickname}/ai-report):
// an LLM analyses the channel's activity for a period. WHICH providers exist
// comes from one config file (src/config/aiProviders.json, mirroring the
// backend's internal/aiconfig/providers.json). Every provider is
// bring-your-own-key (AISettingsDraft.apiKeys below).
export type AIProvider = string;
export const AI_PROVIDERS: AIProvider[] = aiProviderConfig.providers.map((p) => p.id);
export const DEFAULT_AI_PROVIDER: AIProvider = aiProviderConfig.default;
export const AI_PROVIDER_LABEL: Record<AIProvider, string> = Object.fromEntries(aiProviderConfig.providers.map((p) => [p.id, p.label]));
// Every provider needs an API key entered in AI keys (Settings) before it
// can be used - though a request without one may still work when the
// operator configured a shared key server-side.
export const AI_PROVIDER_NEEDS_KEY: Record<AIProvider, boolean> = Object.fromEntries(AI_PROVIDERS.map((p) => [p, true]));

export const AI_REPORT_PERIODS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
] as const;
export type AiReportPeriodDays = (typeof AI_REPORT_PERIODS)[number]['days'];

// AI report jobs: generation runs on a background worker queue, not
// inline. The owner queues a job (optionally "High" priority, which sorts
// it ahead of other same-tier jobs), watches it go queued -> running ->
// done live over the Cue "rj" relay, and can Stop a queued/running one or
// Rerun a finished one. Per-channel, owner-only. See backend zmist/report.
export const AI_JOB_PRIORITIES = ['normal', 'high'] as const;
export type AiJobPriority = (typeof AI_JOB_PRIORITIES)[number];
export const AI_JOB_PRIORITY_LABEL: Record<AiJobPriority, string> = {
  normal: 'Normal',
  high: 'High',
};

export type ReportJobStatus = 'queued' | 'running' | 'done' | 'failed' | 'stopped';
export const REPORT_JOB_ACTIVE: ReportJobStatus[] = ['queued', 'running'];

export interface ReportJob {
  id: string;
  channelId: string;
  provider: AIProvider;
  periodDays: number;
  priority: AiJobPriority;
  tier: string;
  status: ReportJobStatus;
  keySource?: 'local' | 'free' | 'user';
  enqueuedAt: number;
  startedAt?: number;
  finishedAt?: number;
  messageId?: string;
  error?: string;
  // Extra channels (own or public) folded into this report alongside
  // channelId - a Multi-Channel Report. Absent/empty for an ordinary
  // single-channel report. See backend/internal/report/jobs.go's Job.
  additionalChannelIds?: string[];
  // "questions" -> the Reports screen's "Ask questions" flow (answers
  // `questions` instead of the usual sections); "suggest" -> the "Suggest
  // questions"/"Ask AI again" flow (proposes new ones, posts no report);
  // absent/"" -> an ordinary report. See backend/internal/report/
  // generate.go's ModeQuestions/ModeSuggest.
  mode?: 'questions' | 'suggest';
  // The LLM-suggested and/or custom questions this job answers, when mode
  // is "questions"; the questions this job proposed, when mode is
  // "suggest" (set once the job finishes).
  questions?: string[];
}

export const THEME_MODE_LABEL: Record<ThemeMode, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};

export const APP_LANGUAGE_LABEL: Record<AppLanguage, string> = {
  english: 'English',
  ukrainian: 'Ukrainian',
};

export interface Attachment {
  id: ID;
  symbol: string;
  colorHex: string;
  title: string;
}

// A message whose type is REPORT_TYPE is a report (see ReportsScreen.tsx) -
// sent/received exactly like any other message (same POST /signal, same Cue
// relay), just tagged so the Reports tab can filter for it and
// chartBuckets.ts/SignalActivityChart can exclude it from signal-activity
// charts (a report isn't "signal activity" the way a template tap is).
export const REPORT_TYPE = 'report';

// The Output Value calculator's result for a signal (backend internal/outvalue,
// stored as `calc`): a stable machine-readable value AND its display text, the
// measured period (never the ingestion time), who it is about, where it came
// from, and technical evidence. Integration-agnostic - nothing here knows what
// produced the signal. Absent for a signal whose mapping has no `calc` block.
export interface SignalCalc {
  v: number;
  label?: string;
  value: {
    raw?: unknown;
    normalized?: number | string | boolean | null;
    text?: string;
    display?: string;
    type: string; // number|currency|percentage|count|date|datetime|duration|boolean|text|status|unknown
    unit?: string;
    precision?: number;
    scale?: string;
  };
  period?: { type: string; start?: string; end?: string; label?: string; shortLabel?: string; range?: string; frame?: string; timezone?: string };
  entity?: { name?: string; id?: string; ticker?: string };
  source?: { type?: string; label?: string; url?: string; receivedAt?: string };
  metadata?: Record<string, unknown>;
  evidence?: unknown;
  // ok | missing | invalid | ambiguous | incomplete - anything but ok has diagnostics.
  status: string;
  diagnostics?: { code: string; message: string; path?: string }[];
}

export interface Message {
  id: ID;
  sender: string;
  senderId?: string;
  text: string;
  date: string; // ISO
  isMine: boolean;
  // Set when this message was sent by tapping a template signal (its value
  // is the signal's name, e.g. "Deploy") or is a report (REPORT_TYPE) -
  // undefined for a plain typed message. Drives ChartScreen's signal-
  // activity-over-time chart (which excludes REPORT_TYPE - see
  // chartBuckets.ts's isChartableSignal).
  type?: string;
  // The TemplateSignal.number this exact message resolved to, if any -
  // undefined for a plain typed message, and (until the backend fix that
  // added this) also undefined for a signalRef-resolved integration signal
  // even though one really was resolved server-side. This is the
  // AUTHORITATIVE identity for "which Signal is this" - prefer it over
  // matching `type` text against currently-loaded templates (fragile: a
  // name can be renamed, collide across templates, or just not be loaded
  // yet), which remains only a fallback for messages that predate this
  // field or never resolved through the template system (`to: "name"`).
  signalNumber?: string;
  // A number carried alongside a signal tap (e.g. a dollar amount), either
  // typed in at send time (see TemplateSignal.promptForValue) or copied from
  // the signal's own static preset value otherwise - undefined for a plain
  // typed message or a signal with no value at all. Stored as a string (raw
  // user input, not parsed/validated here) - chartBuckets.ts's sumByBucket
  // parses it when charting "by value" instead of by count.
  value?: string;
  // The calculated Output Value (display text, unit, period, source evidence) -
  // see SignalCalc. `value` above stays the legacy plain value.
  calc?: SignalCalc;
  // Free-form tags on the signal (backend `labels`, a JSON string array on
  // the wire - see backend/modules/signal/transitions/signal.go
  // ResolveIngestFields). undefined/[] for a signal with none. Mostly set
  // by integrations; a hand-composed message doesn't carry any yet.
  labels?: string[];
  // Event/observation time - "when the signal was marked as an action"
  // (backend `at`). Distinct from `date` (when the message was composed)
  // and `receivedAt` (server ingest). Falls back to `date` server-side
  // when the client doesn't supply one, so it's effectively always
  // present on a fetched signal.
  at?: string;
  // Set only on a message just composed locally via StoreContext.sendMessage,
  // while its POST /signal persist is still in flight or has failed -
  // undefined means "saved": either it was loaded from GET /signals (so it's
  // already on the server by definition) or its own persist confirmed. Purely
  // a local UI status indicator (see MessagesScreen's sync icon), never sent
  // to or read from the backend.
  syncStatus?: 'pending' | 'failed';
  // An optional file attached to this message - the blob lives in /storage
  // under `key` (fetch it back via fetchStorageDataUrl); `type` is its MIME
  // type, `name` the original filename, `size` the byte count. Undefined
  // when the message has no attachment. Rendered by AttachmentView.
  attachment?: MessageAttachment;
  // Set only on a Multi-Channel Report message - the extra channel ids
  // folded into it alongside the channel it was posted in (see
  // FetchedSignal.additionalChannelIds / internal/report/generate.go's
  // PostReport). Undefined for every other message, including an ordinary
  // single-channel report. Drives ReportsScreen's structured evidence-
  // section rendering (see evidenceReport.ts).
  additionalChannelIds?: string[];
  // Set only on a Multi-Channel Report - the code-computed confidence
  // level ('High'/'Medium'/'Low') and the factors that produced it (see
  // FetchedSignal.confidence/.confidenceFactors). This is the level the
  // backend dictated into the LLM's own prompt, not the LLM's self-
  // assessment - ReportsScreen/PublicChannelViewScreen prefer it over
  // evidenceReport.ts's text-parsed confidenceLevel, which is a fallback
  // only for reports generated before this field existed.
  confidence?: string;
  confidenceFactors?: string[];
  // Set only on a report (see FetchedSignal.number/.edited): its stable
  // per-channel permalink number (0/absent on a manually-typed report or
  // one predating this field - ReportsScreen's inline editor addresses a
  // report by its id instead, which every report has) and whether an
  // owner has edited its text since the AI posted it.
  number?: string;
  edited?: boolean;
  // The id of another message in this same channel this one replies to -
  // undefined for an ordinary message. Backend `replyToId` (see
  // backend/internal/sigfields, stored as short field "rt"), carried live
  // over Cue as the "mr" command (protocol/wire.go's SplitReplyValue).
  // The referenced message is looked up locally by id from the already-
  // loaded feed for the quoted-preview render (MessagesScreen.tsx) - no
  // separate fetch, and a reply whose anchor has since been deleted or
  // isn't loaded just shows no preview.
  replyToId?: ID;
}

export interface MessageAttachment {
  key: string;
  type: string;
  name: string;
  size: number;
}

export interface Post {
  id: ID;
  title: string;
  link: string;
  description: string;
  attachments: Attachment[];
}

export interface WorkflowAction {
  id: ID;
  title: string;
  kind: string;
}

export interface Workflow {
  id: ID;
  title: string;
  nickname: string;
  trigger: string;
  actions: WorkflowAction[];
  active: boolean;
}

export interface TemplateSignal {
  id: ID;
  name: string;
  value?: string;
  // When true, tapping this signal prompts for a value at send time instead
  // of sending immediately - the typed value becomes that message's Message
  // .value (and overrides the static `value` above for that one send). When
  // false/unset, sending is immediate and, if `value` is set, that static
  // value is what carries through to Message.value instead.
  promptForValue?: boolean;
  colorHex: string;
  emoji: string;
  // Server-assigned, global, permanent (backend/workflows/template/
  // create.wsl's AssignSignalNumbers) - the primary way to trigger this
  // exact preset over REST (POST /signal's signalNumber) or Cue (the "mt"
  // command) instead of spelling out name/value by hand. A numeric
  // string, not a number - avoids float64 precision loss on a large
  // int, same convention as every other large-int field this backend
  // returns (see Message.id's comment). Absent until the template has
  // been saved at least once (a brand-new, not-yet-submitted preset in
  // the editor has no number yet).
  number?: string;
}

export interface Template {
  id: ID;
  title: string;
  nickname: string;
  isPublic: boolean;
  colorHex: string;
  emoji: string;
  // See ProfileDraft.avatarImageKey's comment - same convention. Takes
  // precedence over `emoji` for display when set (see PublicTemplateScreen/
  // TemplatesListScreen).
  avatarImageKey: string;
  signals: TemplateSignal[];
  // Real count, backed by GET /stats/template/{nickname}.
  likes: number;
  // This account's own state - see Channel.liked's comment; only fetched
  // where shown (PublicTemplateScreen).
  liked: boolean;
  usages: number;
  // Empty/missing means no owner has been recorded (e.g. a template
  // created before ownership tracking existed) - treated as owned by the
  // current user, matching Channel's isOwner default-true convention.
  ownerId?: string;
}

// An integration Mapping - the server-stored half of a future Zmist
// Integration Tool definition (how data from an external source becomes
// Signals). Phase 1: metadata + an opaque `spec` object the backend
// never interprets. Modelled on Template (nickname-keyed, public/private,
// owner-scoped). See backend/workflows/mapping/*.
export interface Mapping {
  id: ID;
  nickname: string;
  title: string;
  description: string;
  isPublic: boolean;
  // Free-form label for where the data comes from (e.g. "webhook",
  // "prometheus", "google_sheets", "csv") - no enum enforced yet.
  sourceType: string;
  // Opaque config the Integration Tool authors and a daemon consumes.
  // The web app only shows/edits it as raw JSON in Phase 1.
  spec: Record<string, unknown>;
  // Owner nickname. Empty/missing = treated as owned by the current user
  // (same convention as Template.ownerId).
  ownerId?: string;
  // Whether a Prometheus remote_write ingest token is configured for this
  // mapping - never the token or its hash. The backend only ever stores a
  // SHA-256 hash (see backend/modules/mapping/transitions/mapping.go's
  // hashIngestToken), so there is nothing to show on a normal read.
  ingestTokenSet?: boolean;
  // Whether this integration accepts incoming data (POST /mappings/
  // {nickname}/enabled toggles it - see the Integrations list's pause
  // button). Absent on a mapping that predates the field, which reads as
  // enabled - see deriveIntegrationView.
  enabled?: boolean;
  // The RAW ingest token, in the clear - present ONLY in the response
  // right after it was minted (creating a mapping, or POST /mappings/
  // {nickname}/rotate-ingest-token) and never again afterwards. A screen
  // must show/let the user copy this immediately; there is no way to
  // retrieve it later, only to rotate to a new one.
  ingestToken?: string;
  // The webhook connector's HMAC signing secret (github/stripe-style
  // `spec.provider`s - see backend/internal/webhookrecv), in the clear.
  // Unlike ingestToken this is NOT a one-time reveal: the backend stores
  // it (and returns it) unhashed, because verifying an inbound HMAC
  // signature needs the real key, not a hash of it (see
  // backend/modules/mapping/transitions/mapping.go's BuildDoc comment) -
  // so every GET /mapping/{nickname} response carries the current value
  // for the owner, not just the response right after mint/rotate.
  webhookSecret?: string;
}

// The channel-permission matrix (roadmap 4a-f). As a channel field
// (Channel.memberPermissions) these are the per-member defaults a newly
// added member inherits; the same shape rides on ChannelMember.permissions
// as one member's effective override (plus manageChannel there). Every
// flag is materialised server-side, so these are never optional on a
// hydrated object - DEFAULT_MEMBER_PERMISSIONS only seeds a brand-new
// local draft before the first fetch.
export interface MemberPermissions {
  postMessages: boolean;
  uploadAttachments: boolean;
  importCsv: boolean;
  sendReports: boolean;
  // textOnly and signalsOnly are opposing restrictions - the UI keeps them
  // mutually exclusive, and the backend forces signalsOnly off if both
  // arrive on.
  textOnly: boolean;
  signalsOnly: boolean;
}

export const DEFAULT_MEMBER_PERMISSIONS: MemberPermissions = {
  postMessages: true,
  uploadAttachments: true,
  importCsv: true,
  sendReports: true,
  textOnly: false,
  signalsOnly: false,
};

// One member's effective permissions (ChannelMember.permissions) - the
// matrix plus the pre-existing manageChannel co-ownership grant.
export interface ChannelMemberPermissions extends MemberPermissions {
  manageChannel: boolean;
}

export const DEFAULT_CHANNEL_MEMBER_PERMISSIONS: ChannelMemberPermissions = {
  ...DEFAULT_MEMBER_PERMISSIONS,
  manageChannel: false,
};

// Role presets (roadmap 4b-f) - kept in exact sync with the server-side
// catalog in backend/modules/channel/transitions/sharelink.go's
// permissionPresets. Picking one of these is always allowed on any tier;
// setChannelMemberPermissions only throws a LimitError({code:
// "advanced_permissions"}) when the resulting set matches none of them
// (hand-tuned toggles) and ADVANCED_PERMISSIONS_ENABLED is on. "Owner"
// deliberately isn't here - that's channel.ownerIds membership, already
// its own "Co-owner" toggle, not something assigned through this list.
export type PermissionPresetId = 'admin' | 'contributor' | 'commenter' | 'viewer';

export const PERMISSION_PRESETS: { id: PermissionPresetId; label: string; permissions: ChannelMemberPermissions }[] = [
  {
    id: 'admin',
    label: 'Admin',
    permissions: { postMessages: true, uploadAttachments: true, importCsv: true, sendReports: true, textOnly: false, signalsOnly: false, manageChannel: true },
  },
  {
    id: 'contributor',
    label: 'Contributor',
    permissions: { postMessages: true, uploadAttachments: true, importCsv: true, sendReports: true, textOnly: false, signalsOnly: false, manageChannel: false },
  },
  {
    id: 'commenter',
    label: 'Commenter',
    permissions: { postMessages: true, uploadAttachments: false, importCsv: false, sendReports: false, textOnly: true, signalsOnly: false, manageChannel: false },
  },
  {
    id: 'viewer',
    label: 'Viewer',
    permissions: { postMessages: false, uploadAttachments: false, importCsv: false, sendReports: false, textOnly: false, signalsOnly: false, manageChannel: false },
  },
];

// Matches a member's effective permission set to one of the presets above
// (mirrors the backend's presetForPermissions) - "" when it's custom/
// hand-tuned and matches none exactly.
export function presetForPermissions(p: ChannelMemberPermissions): PermissionPresetId | '' {
  const found = PERMISSION_PRESETS.find(
    (preset) =>
      preset.permissions.postMessages === p.postMessages &&
      preset.permissions.uploadAttachments === p.uploadAttachments &&
      preset.permissions.importCsv === p.importCsv &&
      preset.permissions.sendReports === p.sendReports &&
      preset.permissions.textOnly === p.textOnly &&
      preset.permissions.signalsOnly === p.signalsOnly &&
      preset.permissions.manageChannel === p.manageChannel
  );
  return found?.id ?? '';
}

// A channel member doc's status (ChannelMember.status). "invited" = an
// owner invited them, awaiting the invitee's accept/decline; "pending" =
// a self-service join request awaiting owner approval; "accepted" = a
// real member. Kept as a plain string on the wire - this is for docs.
export type ChannelMemberStatus = 'invited' | 'pending' | 'accepted';

// A denormalized snapshot of just the fields a chart-comparison chip/series
// actually reads (SignalActivityChart.tsx, AdditionalChannelsField.tsx) -
// not a live reference. Stored as-is on Channel.chartCompareChannels
// (backend/workflows/channel/{create,update}.wsl) rather than a list of
// ids, since there's no "fetch channels by id" endpoint to re-hydrate ids
// into displayable channels after a reload (only fetchChannel(nickname) -
// see ChannelPickerScreen.tsx's comment) and the alternative - a new batch
// endpoint - is more than this purely-cosmetic overlay needs. A superset
// `Channel` satisfies this structurally, so passing one in (as the picker
// screen does) needs no conversion.
export interface ChannelRef {
  id: ID;
  title: string;
  nickname: string;
  accentHex: string;
}

export interface Channel {
  id: ID;
  title: string;
  nickname: string;
  description: string;
  templateIds: ID[];
  viewType: ChannelViewType;
  viewChartType: ChartVariant;
  viewDesign: ChannelViewDesign;
  // The owner's current Charts-tab "Filters & settings" (interval, metric,
  // signal/sender filters, ...) - see components/charts/SignalActivityChart.tsx's
  // own ChartSettings type for the real shape; kept loose here (not
  // imported) since types.ts is itself imported by that component - see
  // ChartScreen.tsx (writes it, via StoreContext.setChartSettings) and
  // PublicChannelViewScreen.tsx (reads it back as the public chart's
  // initialSettings).
  chartSettings: Record<string, unknown>;
  // Owner opt-in (default off) - when true, a public /shared/{nickname}/
  // {token} visitor gets the same interactive "Filters & settings" panel
  // an owner does instead of a locked, owner-configured-only chart. See
  // SignalActivityChart's `locked` prop.
  publicChartFiltersAllowed: boolean;
  isPublic: boolean;
  // The real userIds that own this channel (see backend/workflows/channel/
  // create.wsl) - starts as [creator], and more equal owners can be added
  // via POST /channels/{nickname}/owners. Empty/absent means no owner was
  // ever recorded, treated as owned by the current user. All owners are
  // equal: each can edit, manage members, and delete. Unlike
  // Template.ownerId (still a single nickname - see that field's own
  // comment on why channel and template deliberately differ). Used by
  // isChannelOwner (StoreContext.tsx) to gate every owner-only action.
  // Used to also have a separate, legacy `isOwner: boolean` field that
  // always defaulted to true regardless of this one - removed entirely
  // once every real call site was confirmed migrated to isChannelOwner(),
  // matching the iOS side's identical cleanup (see Channel.swift).
  ownerIds?: string[];
  accentHex: string;
  avatarSymbol: string;
  // See ProfileDraft.avatarImageKey's comment - same convention (always
  // sent, "" when unset).
  avatarImageKey: string;
  // Real counts, backed by GET /stats/channel/{nickname} (Redis Set
  // cardinality server-side - see backend/workflows/stats/channel_read.wsl).
  followers: number;
  likes: number;
  // This account's own state (does *I* like/follow this channel, not a
  // count) - backed by GET /stats/channel/{nickname}/me, only fetched
  // where it's actually shown (ChannelDetailsScreen), so defaults to false
  // elsewhere rather than being wrong. `following` used to be a `number`
  // here with no real backing data (a channel has no "how many channels IT
  // follows" concept) - repurposed to what the Follow button actually
  // needs instead.
  liked: boolean;
  following: boolean;
  // This account's own push-notification mute state for this channel -
  // same "only fetched where shown" convention as liked/following.
  // Backed by GET /stats/channel/{nickname}/me too (channel_read_me.wsl)
  // and by push:muted:<userId>, a Redis Set of muted channel nicknames the
  // push fan-out service (kuetix/packages/push's audience.go) already
  // checks on every send - see channel/mute.wsl / unmute.wsl for the
  // write side this closes the loop on (docs/CLAUDE_ZMIST_ROADMAP.md's
  // push-notifications item: "checked on every send... but nothing
  // anywhere ever writes to that set").
  muted: boolean;
  // Message history is NOT part of Channel - it lives in the per-channel
  // MessageStore (store/messageStore.ts) so a new signal doesn't re-render
  // everything that reads channels.
  posts: Post[];
  workflows: Workflow[];
  tags: string[];
  // Per-member send-permission defaults (roadmap 4a-f) - what a member
  // added to this channel inherits. Edited on ChannelManageScreen; a
  // single member's overrides live on ChannelMember.permissions and are
  // edited on ChannelShareScreen.
  memberPermissions: MemberPermissions;
  // Owner opt-in for the "reveal round" control (planning poker / hidden
  // voting - see lib/round.ts). Off by default; toggled in the channel
  // edit form. When off, no round controls, banners, or answer masking.
  revealRounds: boolean;
  // Message bunching window in seconds for this channel's chat (0 = off) -
  // see BunchingInterval above. Set in the channel edit form; defaults to
  // 60 (1 min) for a brand-new channel.
  bunchingInterval: BunchingInterval;
  // Extra channels overlaid on this channel's own chart for comparison
  // (ChartScreen.tsx) - persisted on the channel doc (owner-only sync, same
  // as viewChartType via patchChannel) so the selection survives a page
  // reload instead of living only in in-memory store state. See ChannelRef's
  // comment for why this is a denormalized snapshot, not a list of ids.
  chartCompareChannels: ChannelRef[];
  // Extra channels folded into this channel's AI reports as a
  // Multi-Channel Report (ReportsScreen.tsx's "Add channel" picker) -
  // persisted on the channel doc the same way and for the same reason as
  // chartCompareChannels above (survives a reload/other session instead
  // of resetting on every mount).
  reportAdditionalChannels: ChannelRef[];
  // Channel-list sort weight (signed int, default 0). Set ONLY from ZMAP
  // (PATCH /zmap/channels/{nickname} {"priority": N}); a plain channel edit
  // carries it forward untouched, like boost. Lower = higher in every
  // member's list - an admin sets a big negative to pin an announcement
  // channel above everyone's own pins. See the channel-list sort in
  // StoreContext.tsx (sortChannels) and backend/modules/channel/transitions/pins.go.
  priority: number;
  // A 1:1 direct-message conversation (ChannelsListScreen's "start a
  // conversation" flow) rather than an ordinary group channel - fixed at
  // creation, carried forward untouched by every edit (see backend/
  // workflows/channel/update.wsl's identical isPublic/ownerIds comment).
  // The only thing this actually changes server-side: the invited
  // participant is automatically made a co-owner once they accept (see
  // backend/workflows/channel/invite_accept.wsl's AddDirectCoOwner), so
  // both sides of a DM are always equal owners rather than an owner + an
  // ordinary member.
  isDirect?: boolean;
}

export interface ProfileDraft {
  // The account's real, immutable userId (a UUID) - distinct from
  // nickname, which is just a display handle. Needed for anything that
  // has to match this account against a channel's ownerIds or a
  // channel:member:* record, both of which key by userId now, not
  // nickname (see StoreContext.tsx's isChannelOwner comment). Empty
  // string until the first successful profile fetch after login/register.
  userId: string;
  name: string;
  nickname: string;
  phone: string;
  website: string;
  instagram: string;
  whatsapp: string;
  messenger: string;
  telegram: string;
  linkedin: string;
  avatarSymbol: string;
  // The /storage key of an uploaded avatar image, if any (see
  // AvatarImagePicker.tsx) - maps onto the backend's personal.avatar.image
  // (see backend/workflows/user/update.wsl). Empty string, not undefined,
  // when unset - this field is always sent on POST /profile like every
  // other editable field on that endpoint (no partial-update support).
  avatarImageKey: string;
  // Pricing tier id (see AccountTier). Server-owned - never sent on
  // POST /profile; hydrated from GET /profile's `tier.id` and refreshed
  // from GET /billing/me. Defaults to 'free' until the first fetch.
  tier: AccountTier;
  // Server-owned per-account flag (GET /profile's `flags.
  // diagnosticsServerPipeline`, set from ZMAP's Flags tab). When true the
  // Diagnostics screen shows the internal "Server pipeline" section.
  // Defaults false until the first fetch; never sent on POST /profile.
  showServerPipeline: boolean;
}

// Pricing tiers (docs/pricing/CLAUDE_ZMIST_PRICING_FINAL.md). The catalog
// itself - prices, limits, CTA per tier - is server-authoritative and
// fetched via GET /billing/tiers (see billingApi.ts / TierCatalogEntry);
// this union is just the set of known ids for local typing and gating.
// #11 pricing enforcement (2026-09-14, docs/CLAUDE_ZMIST_ROADMAP.md) retired
// 'business' and added 'founding' - see backend/config/tiers.json, the
// authoritative catalog this union mirrors.
export type AccountTier = 'free' | 'founding' | 'team' | 'enterprise';
export const ACCOUNT_TIERS: AccountTier[] = ['free', 'founding', 'team', 'enterprise'];
export const ACCOUNT_TIER_LABEL: Record<AccountTier, string> = {
  free: 'Free',
  founding: 'Founding',
  team: 'Team',
  enterprise: 'Enterprise',
};

export interface TierLimits {
  activeChannels: number;
  storedSignalsPerMonth: number;
  retentionDays: number;
  sustainedRatePerSec: number;
  burstCapacity: number;
  // Multi-Channel Report enqueues (additionalChannelIds non-empty) allowed
  // per ISO week; -1 = unlimited (fair use). See internal/report's
  // CheckMultiChannelQuota.
  multiChannelReportsPerWeek: number;
}

// One row of the server pricing catalog (GET /billing/tiers). `cta` is the
// action a client should offer: 'current' | 'checkout' | 'contact'.
// A price of -1 means "not publicly priced" (Enterprise = contact sales).
export interface TierCatalogEntry {
  id: AccountTier;
  name: string;
  tagline: string;
  priceMonthly: number;
  priceYearly: number;
  badgeRemovable: boolean;
  cta: 'current' | 'checkout' | 'contact';
  limits: TierLimits;
}

export type BillingInterval = 'monthly' | 'yearly';

// The caller's live billing state (GET /billing/me).
export interface BillingState {
  tier: {
    id: AccountTier;
    status: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  };
  entitlements: {
    name: string;
    priceMonthly: number;
    priceYearly: number;
    badgeRemovable: boolean;
    limits: TierLimits;
  };
  usage: {
    ownedChannels: number;
  };
}

export interface SecurityDraft {
  key: string;
  secret: string;
}

export interface AISettingsDraft {
  // Which provider to use for AI reports.
  provider: AIProvider;
  // Per-provider API keys. Empty for a provider = none saved. Stored only in this browser's localStorage,
  // sent per-request, never persisted server-side.
  apiKeys: Record<AIProvider, string>;
}

export const emptyAiKeys = (): Record<AIProvider, string> =>
  AI_PROVIDERS.reduce((acc, p) => ({ ...acc, [p]: '' }), {} as Record<AIProvider, string>);

export interface AiReportUsage {
  used: number;
  limit: number;
  remaining: number;
}

// Per-provider call/token usage (backend/internal/report/ai_usage.go's
// AIUsageStats) - "total" is a synthetic provider id for the summed
// bucket across every real provider.
export interface AiUsageStat {
  calls: number;
  tokensIn: number;
  tokensOut: number;
}
export type AiUsageDetailed = Partial<Record<AIProvider | 'total', AiUsageStat>>;
